import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { rxResource } from "@angular/core/rxjs-interop";
import { catchError, of } from "rxjs";
import { ApiService } from "../core/api.service";
import { ConfirmDialogService } from "../core/confirm-dialog.service";

interface Supplier {
  id: string;
  name: string;
  email: string;
  createdAt: string;
}

@Component({
  selector: "app-suppliers-page",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, RouterModule],
  template: `
    <section class="dashboard">
      <div class="dashboard-main">
        <header class="dashboard-header">
          <div>
            <h1>Suppliers</h1>
            <p class="header-subtitle">
              Create supplier accounts for the supplier portal.
            </p>
          </div>
          <div class="header-actions">
            <a class="action-button" routerLink="/settings">← Back</a>
          </div>
        </header>

        <div class="panel-card">
          <div class="panel-header">
            <h2>Add supplier</h2>
            <p class="panel-description">
              A login link + temporary password is emailed to the supplier
              automatically.
            </p>
          </div>
          <div class="panel-content">
            <div class="supplier-add">
              <input
                class="form-input"
                [(ngModel)]="newName"
                placeholder="Name"
              />
              <input
                class="form-input"
                [(ngModel)]="newEmail"
                type="email"
                placeholder="Email"
              />
              <button
                class="action-button primary"
                (click)="addSupplier()"
                [disabled]="saving()"
              >
                Add
              </button>
            </div>
          </div>
        </div>

        <div class="panel-card">
          <div class="panel-header">
            <h2>All suppliers</h2>
            <p class="panel-description">
              {{ suppliers()?.length ?? 0 }} total
            </p>
          </div>
          <div class="panel-content">
            @if (suppliers()?.length === 0) {
              <p class="empty-message">No suppliers yet. Add one above.</p>
            }
            @for (supplier of suppliers(); track supplier.id) {
              <div class="supplier-row">
                <div class="supplier-info">
                  <span class="supplier-name">{{ supplier.name }}</span>
                  <span class="supplier-email">{{ supplier.email }}</span>
                </div>
                <div class="row-actions">
                  <button
                    class="chip-btn danger"
                    (click)="deleteSupplier(supplier)"
                    title="Delete"
                  >
                    ×
                  </button>
                </div>
              </div>
            }
            @if (message()) {
              <div class="status-message">{{ message() }}</div>
            }
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [
    `
      .dashboard { display: grid; grid-template-columns: 1fr; gap: 24px; align-items: start; }
      .dashboard-main { min-width: 0; }
      .dashboard-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; flex-wrap: wrap; gap: 16px; }
      .dashboard-header h1 { font-size: 32px; margin: 0; }
      .header-subtitle { color: var(--ink-500); margin: 4px 0 0; font-size: 14px; }
      .header-actions { display: flex; gap: 10px; align-items: center; }
      .action-button {
        border: none; background: var(--panel); color: var(--ink-900); padding: 10px 16px;
        border-radius: 12px; font-weight: 600; font-size: 14px; cursor: pointer;
        box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08); text-decoration: none;
        display: inline-flex; align-items: center;
      }
      .action-button.primary { background: #111827; color: #ffffff; box-shadow: 0 8px 20px rgba(17, 24, 39, 0.15); }
      .action-button:disabled { opacity: 0.5; cursor: not-allowed; }
      .panel-card { background: var(--panel); border-radius: 20px; padding: 24px; box-shadow: 0 14px 32px rgba(15, 23, 42, 0.1); margin-bottom: 24px; }
      .panel-header { margin-bottom: 18px; }
      .panel-header h2 { font-size: 18px; margin: 0; color: var(--ink-900); }
      .panel-description { font-size: 13px; color: var(--ink-500); margin: 4px 0 0; }
      .panel-content { display: grid; gap: 12px; }
      .supplier-add { display: grid; grid-template-columns: 1fr 1fr 1fr auto; gap: 8px; }
      .form-input { padding: 10px 12px; border: 1px solid rgba(15, 23, 42, 0.12); border-radius: 10px; font-size: 14px; font-family: inherit; color: var(--ink-900); background: #fafbfc; }
      .form-input:focus { outline: none; border-color: #111827; background: #ffffff; box-shadow: 0 0 0 3px rgba(17, 24, 39, 0.08); }
      .supplier-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 10px 0; border-bottom: 1px solid rgba(15, 23, 42, 0.08); }
      .supplier-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
      .supplier-name { font-weight: 600; font-size: 14px; color: var(--ink-900); }
      .supplier-email { font-size: 12px; color: var(--ink-500); overflow-wrap: anywhere; }
      .row-actions { display: flex; gap: 4px; flex-wrap: wrap; }
      @media (max-width: 600px) {
        .supplier-add { grid-template-columns: 1fr; }
        .supplier-row { flex-wrap: wrap; }
      }
      .chip-btn { border: none; background: transparent; cursor: pointer; color: var(--ink-500); font-size: 13px; padding: 2px 4px; }
      .chip-btn.danger { color: #dc2626; }
      .chip-btn:hover { background: rgba(15, 23, 42, 0.06); border-radius: 6px; }
      .empty-message { color: var(--ink-500); font-size: 13px; }
      .status-message { color: var(--ink-500); font-size: 13px; padding: 12px; background: #fef3c7; border-radius: 10px; margin-top: 12px; }
    `,
  ],
})
export class SuppliersPageComponent {
  private readonly api = inject(ApiService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly saving = signal(false);
  readonly message = signal("");
  newName = "";
  newEmail = "";

  readonly suppliersResource = rxResource({
    stream: () =>
      this.api.listSuppliers().pipe(catchError(() => of([] as Supplier[]))),
  });
  readonly suppliers = this.suppliersResource.value;

  addSupplier() {
    const name = this.newName.trim();
    const email = this.newEmail.trim();
    if (!name || !email) {
      this.message.set("Name and email are required.");
      return;
    }
    this.saving.set(true);
    this.api.createSupplier({ name, email }).subscribe({
      next: (created) => {
        this.suppliersResource.update((list) =>
          [...(list ?? []), created].sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
          ),
        );
        this.newName = "";
        this.newEmail = "";
        this.saving.set(false);
        this.message.set("Supplier added. Login details emailed.");
      },
      error: (err) => {
        this.message.set(err?.error?.message ?? "Failed to add supplier.");
        this.saving.set(false);
      },
    });
  }

  async deleteSupplier(supplier: Supplier) {
    const confirmed = await this.confirmDialog.confirm({
      title: "Delete supplier",
      message: `Delete supplier "${supplier.name}"?`,
      confirmText: "Delete",
      destructive: true,
    });
    if (!confirmed) return;
    this.api.deleteSupplier(supplier.id).subscribe({
      next: () => {
        this.suppliersResource.update((list) =>
          (list ?? []).filter((s) => s.id !== supplier.id),
        );
        this.message.set("Supplier deleted.");
      },
      error: (err) =>
        this.message.set(err?.error?.message ?? "Failed to delete supplier."),
    });
  }
}
